import '@/styles/styles.scss';
// import 'antd/dist/antd.css';

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useRoutes, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';

import store from '@/store';
import routes from '@/routes/routes';
import { RouterAuth, screenRoutesByRole } from '@/routes/index';

import i18next from './i18n';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import da from 'date-fns/locale/da';
import en from 'date-fns/locale/en-US';

import ConfigGate from '@/components/core/config-gate/config-gate';
import { setRuntimeConfig, selectRuntimeConfig } from '@/store/configuration';
import configurationApi from '@/services/configuration.api';
import legalApi, { legalContentLang } from '@/services/legal.api';
import Token from '@/utils/token';
import { LANGUAGE } from '@/constants/system';
import { mockConfigByTenant } from '@/mocks/configuration.mock';
import { useApplyRuntimeTheme } from '@/hooks/useApplyRuntimeTheme';
import { useApplyFavicon } from '@/hooks/useApplyFavicon';
import { useRuntimeConfig } from '@/hooks/useRuntimeConfig';
import DefaultSpinnerContainer from '@/components/core/spinners/default-spinner-container/default-spinner-container';
import { ToastsContainer } from '@/components/core/toasts-container/toasts-container';
import FkoStatusModal from '@/components/fko-status-modal/fko-status-modal';
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { environment } from '@/environments/environment';
import { useOfflineToast } from '@/hooks/useOfflineToast';

setDefaultLocale(en);
registerLocale('da', da);

const App: FC = () => {
  useApplyRuntimeTheme();
  useApplyFavicon();
  const config = useRuntimeConfig();
  const { role } = store.getState().user;
  const curRoutes = useMemo(() => screenRoutesByRole(routes), [role]);
  const Element = useRoutes(curRoutes);
  useOfflineToast();

  const firebaseConfig = config?.exterior?.firebase;

  // Initialize Firebase and Google Analytics
  if (firebaseConfig) {
    const app = initializeApp(firebaseConfig);
    const analytics = getAnalytics(app);
    const location = useLocation();
    useEffect(() => {
      logEvent(analytics, 'page_view');
      // add other route change related track here
    }, [location]);
  }

  return (
    <ToastsContainer>
      {/* Fragment because ToastsContainer types its children as a SINGLE
          element. App root, alongside the toasts: the notice outlives the
          login page (advanceLoginStep lands with an SPA navigate) and must not
          be mounted per-route, which would give it one instance per nested
          container. */}
      <>
        <FkoStatusModal />
        <RouterAuth>{Element}</RouterAuth>
      </>
    </ToastsContainer>
  );
};

export const root = document.getElementById('app');

const renderApp = () => {
  createRoot(root!).render(
    <I18nextProvider i18n={i18next}>
      <Provider store={store}>
        <ConfigGate>
          <DefaultSpinnerContainer>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </DefaultSpinnerContainer>
        </ConfigGate>
      </Provider>
    </I18nextProvider>
  );
};

// Load the tenant runtime config before the first render (ADR-07 step ①).
// - configSource 'live' (all new-world builds: local / develop / production) or
//   ?config=live: same-origin GET /api/v1/configuration. The endpoint is served
//   by the tenant's own module (rpms #115 moved it off ufs and deleted the
//   forwarding hop); the handler does not read Host, it just projects its own
//   config.yaml. Deployed builds sit on <env>.<tenant>.levvel.cloud and APISIX
//   routes the whole host to that tenant's module; locally the devServer proxy
//   points /api at one module's port, so the tenant is whichever port you proxy
//   to (see webpack.config.local.babel.js). matchFulfilled populates the store.
// - everything else (today: only legacy builds, via the compat read below):
//   boot from the local NK mock.
// ConfigGate still blocks render until the config lands, so no consumer ever
// reads a half-configured app; on fetch failure it keeps the app gated.
const bootstrapConfig = async () => {
  const params = new URLSearchParams(window.location.search);
  const live = params.get('config') === 'live';
  // 2026-09-06 起这里只读 configSource:遗产 environment.*.ts 已全部删除,那条按
  // production / isLocalDev 双 flag 的宽化兼容读取(它们表达的是同一件事)随之取消,
  // 按当时留下的约定「遗产 env 文件退场时连同这行一起删」执行。
  if (environment.configSource === 'live' || live) {
    // Real endpoint: same-origin, tenant resolved from Host by APISIX routing.
    await store.dispatch(
      configurationApi.endpoints.getConfiguration.initiate()
    );
  } else {
    // dev-only: ?tenant=mvh switches mock to demo per-tenant render; default NK.
    const tenant = params.get('tenant');
    store.dispatch(setRuntimeConfig(mockConfigByTenant(tenant)));
  }

  // Apply the tenant's default language, unless the user already picked one.
  const cfg = selectRuntimeConfig(store.getState());
  if (!localStorage.getItem(LANGUAGE) && cfg?.style?.defaultLanguage) {
    await i18next.changeLanguage(cfg.style?.defaultLanguage);
  }

  // Legal documents ride along with the runtime config (batch 004, ADR-5) so the
  // sidebar's Terms/Privacy links are present on the FIRST frame rather than
  // popping in a beat later — the behaviour they replace blocked the whole app
  // render on the tenant custom-info request, and this repo has an explicit
  // no-flash stance. Deliberately AFTER the language is applied, because the list
  // is per-language.
  //
  // Note this covers the reload-while-logged-in path only. bootstrapConfig runs
  // before the first render, and a fresh login inside an already-loaded page is an
  // SPA navigation (advanceLoginStep -> navigate('/')), so this will not re-run for
  // it — authed-container triggers the same fetch for that case. See the ADR-5
  // promote-time reconciliation note.
  await bootstrapLegalDocuments();
};

// Only meaningful with a terminal token in hand: the endpoint is authenticated
// (tier-1), and the token is not in the store yet at this point in the boot — it is
// rehydrated later, in authed-container. Read it from localStorage and pass it
// explicitly; on the login page there is none, so this is simply skipped rather
// than firing a request that can only 401.
const bootstrapLegalDocuments = async () => {
  const token = Token.getAccessToken();
  if (!token) {
    return;
  }
  await store.dispatch(
    legalApi.endpoints.legalDocuments.initiate({
      // Wire token, not the ISO code — the BE matches content rows exactly and they
      // hold "English"/"Danish". See legalContentLang.
      lang: legalContentLang(i18next.language),
      token,
    })
  );
};

bootstrapConfig().finally(renderApp);
